<template>
  <b-modal :id="id" hide-header hide-footer centered no-close-on-backdrop>
    <template #default="{ close }">
      <b-row class="d-flex m-1 justify-content-end">
        <div @click="onCancel(close)"><b-icon-x class="x-btn"></b-icon-x></div>
      </b-row>
      <b-row no-gutters>
        <div class="category-edit-title-label">カテゴリー名編集</div>
      </b-row>
      <b-row no-gutters>
        <b-form-input
          input="text"
          class="category-title-input"
          v-model="titleSource"
          aria-describedby="title-feedback"
          required
        />
        <b-form-text id="title-feedback" :class="titleState ? 'success' : 'warning'">
          {{ validTitleMessage }}
        </b-form-text>
      </b-row>
      <b-row class="mt-5" align-h="end" no-gutters>
        <cancel class="col-4 mr-auto" v-on:cancel="onCancel(close)" label="キャンセル" />
        <custom-button
          class="col-3 mr-2"
          label="削除"
          v-on:confirm="onDelete(close)"
          variant="danger"
        />
        <confirm class="col-3" v-on:confirm="onEdit(close)" label="保存" :disabled="!titleState" />
      </b-row>

      <b-modal id="category-delete-error" hide-header hide-footer centered>
        <template #default="{ ok }">
          <b-row no-gutters>
            <div>下記話題で利用中のため削除できません</div>
            <b-form-textarea rows="3" class="category-textarea" v-model="errorTitles" readonly />
          </b-row>
          <b-row class="pt-2 flex-row-reverse" no-gutters>
            <confirm class="col-3" label="OK" v-on:confirm="ok" />
          </b-row>
        </template>
      </b-modal>
    </template>
  </b-modal>
</template>
<style scoped>
.category-edit-title-label {
  padding-bottom: 8px;
  font-weight: bold;
  font-size: 14px;
}

.category-title-input {
  background-color: #f3f3f3;
  border-radius: 3px;
  font-size: 14px;
}

.category-textarea {
  font-size: 14px;
  background-color: #f3f3f3;
}
</style>
<script lang="ts">
import { computed, defineComponent, getCurrentInstance, ref, watch, type PropType } from "vue";
import type Vue from "vue";
import { useTopicStore } from "@/store";
import { Category, Topic, User } from "../model";
import Confirm from "./button/Confirm.vue";
import Cancel from "./button/Cancel.vue";
import CustomButton from "./button/CustomButon.vue";
import { useTextProcess } from "@/composables/useTextProcess";
import { useEditDiscard } from "@/composables/useEditDiscard";
import { useLegacyRootEvent } from "@/composables/useLegacyRootEvent";

// カテゴリータイトルの最大文字数
const CATEGORY_TITLE_MAX_LENGTH = 20;

export default defineComponent({
  name: "category-title-edit",
  components: {
    Confirm,
    Cancel,
    CustomButton,
  },
  props: {
    id: { type: String, default: () => "", required: true },
    category: {
      type: Object as PropType<Category>,
      default: () => Category.createTemp(""),
      required: true,
    },
    categories: { type: Array as PropType<Category[]>, default: () => [], required: true },
    domainId: { type: String, default: () => "" },
    // ログインユーザー情報
    me: { type: Object as PropType<User>, default: () => User.createNotFoundUser() },
  },
  setup(props) {
    // 旧 TextProcessMixin / EditDiscardMixin → composable（1-D 利用側書き換え）
    const { getLength } = useTextProcess();
    const { changeEditMode, showEditDiscardAlert } = useEditDiscard();
    const rootEvent = useLegacyRootEvent();
    // $bvModal は BootstrapVue が生やす（Step 3 で composable 化予定）
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    const titleSource = ref<string>("");
    const errorTitles = ref<string>(""); // カテゴリーがセットされた話題のタイトル郡

    watch(
      () => props.category,
      (value: Category): void => {
        titleSource.value = value.title;
      },
      { immediate: true }
    );

    const duplicated = computed((): boolean => {
      if (props.category.title == titleSource.value) return false;
      else {
        return props.categories.some((c) => c.title == titleSource.value);
      }
    });

    const disabled = computed((): boolean => {
      return titleSource.value.length == 0;
    });

    const disabledDelete = computed((): boolean => {
      return props.me.id != props.category.owner;
    });

    const titleState = computed((): boolean => {
      return (
        0 < getLength(titleSource.value) &&
        getLength(titleSource.value) <= CATEGORY_TITLE_MAX_LENGTH &&
        !duplicated.value
      );
    });

    const validTitleMessage = computed((): string => {
      if (duplicated.value) return "既に同じものが登録されています";
      else {
        return getLength(titleSource.value)
          ? `カテゴリー名は${CATEGORY_TITLE_MAX_LENGTH}文字以内です(${getLength(titleSource.value)}/${CATEGORY_TITLE_MAX_LENGTH})`
          : "カテゴリーを入力してください";
      }
    });

    // このカテゴリーが話題に設定されているか
    const isSet = (): boolean => {
      const topics = useTopicStore().get(props.domainId, true) as Topic[]; // 削除されていない話題
      if (!topics) return false;
      const setTopics = topics.filter((topic) => {
        const category = topic.category;
        return category.id == props.category.id;
      });
      errorTitles.value = setTopics
        .map((t) => {
          return t.title;
        })
        .join("\n");
      return setTopics.length > 0;
    };

    const onCancel = (closeEvent: () => void): void => {
      const close = () => {
        titleSource.value = props.category.title;
        closeEvent();
      };

      changeEditMode(titleSource.value !== props.category.title);
      showEditDiscardAlert(close);
    };

    const onEdit = (closeEvent: () => void): void => {
      rootEvent.emit("edited-category", { id: props.category.id, title: titleSource.value });
      closeEvent();
    };

    const onDelete = (closeEvent: () => void): void => {
      if (isSet()) {
        vm?.$bvModal.show("category-delete-error");
      } else {
        rootEvent.emit("deleted-category", { id: props.category.id });
        closeEvent();
      }
    };

    return {
      titleSource,
      errorTitles,
      duplicated,
      disabled,
      disabledDelete,
      titleState,
      validTitleMessage,
      isSet,
      onCancel,
      onEdit,
      onDelete,
    };
  },
});
</script>
